import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Renders a legal document from src/legal/legalContent.json.
 *
 * That file is GENERATED - the canonical copy is `src/legal/legalContent.json`
 * at the repository root, which the iOS app renders from the same data. Edit
 * the canonical file and run `npm run legal:sync` from the repo root; the app's
 * jest suite fails when the two differ, so a forgotten sync cannot merge.
 *
 * This site and the app used to keep separate wordings of the same documents,
 * and they had drifted into contradicting each other about what happens to a
 * user's photos. One source, two renderers, is the fix.
 */

const Block = ({ block }) => {
  switch (block.type) {
    case 'text':
      return <p>{block.text}</p>;
    case 'subheading':
      return <h3>{block.text}</h3>;
    case 'bullets':
      return (
        <ul>
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );
    case 'link':
      return (
        <p>
          <Link to={block.route} className="inline-link">
            {block.label} →
          </Link>
        </p>
      );
    case 'contacts':
      return (
        <div className="contact-info">
          {block.items.map((contact) => (
            <p key={contact.value}>
              <strong>{contact.label}:</strong> {contact.value}
            </p>
          ))}
        </div>
      );
    default:
      return null;
  }
};

const heading = (section) => (section.number ? `${section.number}. ${section.title}` : section.title);

const LegalDocument = ({ document: doc, lastUpdated }) => (
  <div className="terms-page">
    <div className="container">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="page-header">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1>{doc.title}</h1>
          <p className="last-updated">Last updated: {lastUpdated}</p>
        </div>

        <div className="terms-content">
          {/* The contents is derived from the sections, never kept beside
              them: the app shipped a contents list advertising six sections
              that did not exist anywhere on the page. */}
          <nav className="terms-section legal-toc" aria-label="Contents">
            <h2>Contents</h2>
            <ul>
              {doc.sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="inline-link">
                    {heading(section)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {doc.sections.map((section) => (
            <section className="terms-section" id={section.id} key={section.id}>
              <h2>{heading(section)}</h2>
              {section.blocks.map((block, index) => (
                <Block key={`${section.id}-${index}`} block={block} />
              ))}
            </section>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

export default LegalDocument;
